import { create } from 'zustand';
import type { IsoMatch, RotationMode, Turn } from '#shared/domain/drills/index';
import type { CombineLevel, DrillAttempt } from '#shared/schemas/drill';
import { kvDel, kvGet, kvSet } from '@/lib/idb';

/**
 * What this phone knows about the drills it is recording. The outbox is a transit queue (accepted
 * shots leave it), so the recorder keeps its own copy of every shot: it is what the screen folds,
 * it survives a reload with no signal, and it is the source of the results screen. Saved to
 * IndexedDB (`combine:slot:<id>`), one slot per drill session, plus a small index so Home and the
 * hub can offer "Resume".
 *
 * Nothing in here talks to the network. `bridge.ts` writes the outbox and reconciles the answers.
 */

export interface IsoGameRef {
  gameId: string;
  a: string;
  b: string;
  round: number;
}

export interface LocalSession {
  id: string;
  level: CombineLevel;
  /** Shooting order when the level started. */
  playerIds: string[];
  turns: Turn[];
  mode: RotationMode;
  entry: 'tap' | 'fast';
  startedAt: number;
  /** Iso Gauntlet: the pairings and the games started from them. */
  iso?: { matches: IsoMatch[]; games: IsoGameRef[] };
}

export interface FinishState {
  status: 'final' | 'abandoned';
  endedAt: number;
  /** queued: the finish call waits for signal. done: the server has it. */
  state: 'queued' | 'done';
}

export interface Slot {
  session: LocalSession | null;
  /** Every shot written on this phone and not taken back, in order. */
  attempts: DrillAttempt[];
  /** Undo groups: the ids written by one tap (one shot, or one fast-entry set). */
  groups: string[][];
  /** Last seq used on this phone for this session (per device per session, from 1). */
  seq: number;
  /** Ids the server has accepted or already had. */
  synced: string[];
  /** Ids taken back on this phone, so a late "accepted" answer can be voided. */
  undone: string[];
  /** Shots the server has that the phone took back and could not void yet. */
  pendingVoids: string[];
  finish: FinishState | null;
  /** Shots the server turned down. */
  rejected: number;
  loaded: boolean;
}

const empty = (): Slot => ({
  session: null,
  attempts: [],
  groups: [],
  seq: 0,
  synced: [],
  undone: [],
  pendingVoids: [],
  finish: null,
  rejected: 0,
  loaded: false,
});

type Saved = Omit<Slot, 'loaded'>;

export interface IndexEntry {
  id: string;
  level: CombineLevel;
  at: number;
}

const slotKey = (id: string) => `combine:slot:${id}`;
const INDEX_KEY = 'combine:index';
const LAST_KEY = 'combine:last';

export interface CombineState {
  slots: Record<string, Slot>;
  index: IndexEntry[];
  indexLoaded: boolean;
  load(id: string): Promise<void>;
  loadIndex(): Promise<void>;
  start(session: LocalSession): Promise<void>;
  addAttempts(id: string, attempts: DrillAttempt[], seq: number, group?: boolean): void;
  /** Take back the last write. Returns what was removed. */
  undoLast(id: string): DrillAttempt[];
  dropAttempts(id: string, ids: readonly string[]): void;
  setTurns(id: string, turns: Turn[]): void;
  markSynced(id: string, ids: readonly string[]): void;
  forgetUndone(id: string, ids: readonly string[]): void;
  addVoid(id: string, attemptId: string): void;
  removeVoid(id: string, attemptId: string): void;
  setFinish(id: string, finish: FinishState | null): void;
  addRejected(id: string, n: number): void;
  setIsoGames(id: string, games: IsoGameRef[]): void;
  forget(id: string): Promise<void>;
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleSave(id: string): void {
  if (timers.has(id)) return;
  timers.set(
    id,
    setTimeout(() => {
      timers.delete(id);
      const s = useCombineStore.getState().slots[id];
      if (!s) return;
      const { loaded: _loaded, ...saved } = s;
      void kvSet(slotKey(id), saved satisfies Saved).catch(() => {});
    }, 0),
  );
}

function saveIndex(index: IndexEntry[]): void {
  void kvSet(INDEX_KEY, index).catch(() => {});
}

export const useCombineStore = create<CombineState>((set, get) => {
  const update = (id: string, fn: (s: Slot) => Slot, save = true) => {
    const cur = get().slots[id] ?? empty();
    set({ slots: { ...get().slots, [id]: fn(cur) } });
    if (save) scheduleSave(id);
  };

  return {
    slots: {},
    index: [],
    indexLoaded: false,

    async load(id) {
      if (get().slots[id]?.loaded) return;
      let saved: Saved | undefined;
      try {
        saved = await kvGet<Saved>(slotKey(id));
      } catch {
        saved = undefined;
      }
      update(
        id,
        (s) => ({
          ...(saved ? { ...empty(), ...saved } : s),
          // Whatever happened in memory while the read was pending wins.
          session: s.session ?? saved?.session ?? null,
          attempts: s.attempts.length ? s.attempts : (saved?.attempts ?? []),
          loaded: true,
        }),
        false,
      );
    },

    async loadIndex() {
      if (get().indexLoaded) return;
      let index: IndexEntry[] = [];
      try {
        index = (await kvGet<IndexEntry[]>(INDEX_KEY)) ?? [];
      } catch {
        index = [];
      }
      const merged = [...get().index, ...index.filter((e) => !get().index.some((x) => x.id === e.id))];
      set({ index: merged, indexLoaded: true });
      await Promise.all(merged.map((e) => get().load(e.id)));
    },

    async start(session) {
      update(session.id, () => ({ ...empty(), session, loaded: true }));
      const index = [
        { id: session.id, level: session.level, at: session.startedAt },
        ...get().index.filter((e) => e.id !== session.id),
      ];
      set({ index });
      saveIndex(index);
    },

    addAttempts(id, attempts, seq, group = true) {
      update(id, (s) => ({
        ...s,
        attempts: [...s.attempts, ...attempts],
        groups: group ? [...s.groups, attempts.map((a) => a.id)] : s.groups,
        seq: Math.max(s.seq, seq),
      }));
    },

    undoLast(id) {
      const s = get().slots[id];
      const last = s?.groups[s.groups.length - 1];
      if (!s || !last) return [];
      const gone = new Set(last);
      const removed = s.attempts.filter((a) => gone.has(a.id));
      update(id, (cur) => ({
        ...cur,
        attempts: cur.attempts.filter((a) => !gone.has(a.id)),
        groups: cur.groups.slice(0, -1),
        undone: [...cur.undone, ...last],
      }));
      return removed;
    },

    dropAttempts(id, ids) {
      const gone = new Set(ids);
      update(id, (s) => ({
        ...s,
        attempts: s.attempts.filter((a) => !gone.has(a.id)),
        groups: s.groups.map((g) => g.filter((x) => !gone.has(x))).filter((g) => g.length > 0),
      }));
    },

    setTurns(id, turns) {
      update(id, (s) => (s.session ? { ...s, session: { ...s.session, turns } } : s));
    },

    markSynced(id, ids) {
      if (ids.length === 0) return;
      update(id, (s) => ({ ...s, synced: [...new Set([...s.synced, ...ids])] }));
    },

    forgetUndone(id, ids) {
      const gone = new Set(ids);
      update(id, (s) => ({ ...s, undone: s.undone.filter((x) => !gone.has(x)) }));
    },

    addVoid(id, attemptId) {
      update(id, (s) => ({ ...s, pendingVoids: [...new Set([...s.pendingVoids, attemptId])] }));
    },

    removeVoid(id, attemptId) {
      update(id, (s) => ({ ...s, pendingVoids: s.pendingVoids.filter((x) => x !== attemptId) }));
    },

    setFinish(id, finish) {
      update(id, (s) => ({ ...s, finish }));
    },

    addRejected(id, n) {
      update(id, (s) => ({ ...s, rejected: s.rejected + n }));
    },

    setIsoGames(id, games) {
      update(id, (s) => (s.session?.iso ? { ...s, session: { ...s.session, iso: { ...s.session.iso, games } } } : s));
    },

    async forget(id) {
      const { [id]: _gone, ...rest } = get().slots;
      const index = get().index.filter((e) => e.id !== id);
      set({ slots: rest, index });
      saveIndex(index);
      await kvDel(slotKey(id)).catch(() => {});
    },
  };
});

/** Sessions on this phone with no finish yet, newest first. */
export function liveSlots(state: Pick<CombineState, 'slots' | 'index'>): { id: string; session: LocalSession }[] {
  return state.index
    .map((e) => ({ id: e.id, slot: state.slots[e.id] }))
    .filter(
      (x): x is { id: string; slot: Slot & { session: LocalSession } } => !!x.slot?.session && x.slot.finish === null,
    )
    .map((x) => ({ id: x.id, session: x.slot.session }))
    .sort((a, b) => b.session.startedAt - a.session.startedAt);
}

/** Who shot last on this phone, for "the next level starts with the person who went last". */
export async function readLastShooter(now: number): Promise<string | null> {
  try {
    const v = await kvGet<{ playerId: string; at: number }>(LAST_KEY);
    return v && now - v.at < 6 * 3_600_000 ? v.playerId : null;
  } catch {
    return null;
  }
}

export function writeLastShooter(playerId: string, at: number): void {
  void kvSet(LAST_KEY, { playerId, at }).catch(() => {});
}

/** Drop slots that are finished and old. Called once from init(). */
export async function pruneSlots(now: number, maxAgeMs = 3 * 86_400_000): Promise<void> {
  const st = useCombineStore.getState();
  await st.loadIndex();
  const { slots, index } = useCombineStore.getState();
  for (const e of index) {
    const s = slots[e.id];
    const done = s?.finish?.state === 'done' && s.pendingVoids.length === 0;
    if (!s?.session || (done && now - e.at > maxAgeMs) || now - e.at > 14 * 86_400_000) await st.forget(e.id);
  }
}
